import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Calendar, RefreshCw, ExternalLink, Check, Copy,
  Home, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCalendars() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [editingCabin, setEditingCabin] = useState(null);
  const [editUrls, setEditUrls] = useState({ airbnb_url: "", vrbo_url: "", booking_url: "" });
  const [copiedUrl, setCopiedUrl] = useState(null);

  useEffect(() => {
    const role = sessionStorage.getItem("userRole");
    if (role !== "admin") {
      navigate("/admin");
    } else {
      loadCalendars();
    }
  }, [navigate]);

  const loadCalendars = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/calendars`);
      const data = await res.json();
      setCalendars(data);
    } catch (error) {
      console.error("Error loading calendars:", error);
      toast.error("Failed to load calendar settings");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cal) => {
    setEditingCabin(`${cal.hotel_id}/${cal.cabin_id}`);
    setEditUrls({
      airbnb_url: cal.airbnb_url || "",
      vrbo_url: cal.vrbo_url || "",
      booking_url: cal.booking_url || ""
    });
  };

  const handleSave = async (hotelId, cabinId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/calendars/${hotelId}/${cabinId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editUrls)
      });
      
      if (res.ok) {
        toast.success("Calendar URLs saved");
        setEditingCabin(null);
        loadCalendars();
      } else {
        toast.error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save calendar URLs");
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncing(true);
      const res = await fetch(`${API_URL}/api/admin/calendars/sync`, { method: "POST" });
      const data = await res.json();
      
      const totalImported = data.results.reduce((sum, r) => sum + r.bookings_imported, 0);
      toast.success(`Synced ${totalImported} bookings`);
      loadCalendars();
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncCabin = async (hotelId, cabinId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/calendars/sync/${hotelId}/${cabinId}`, { 
        method: "POST" 
      });
      const data = await res.json();
      
      if (data.errors?.length > 0) {
        toast.warning(`Imported ${data.bookings_imported} bookings with errors: ${data.errors.join(", ")}`);
      } else {
        toast.success(`Imported ${data.bookings_imported} bookings`);
      }
      loadCalendars();
    } catch (error) {
      toast.error("Sync failed");
    }
  };

  const copyExportUrl = (url) => {
    navigator.clipboard.writeText(`${API_URL}${url}`);
    setCopiedUrl(url);
    toast.success("Export URL copied to clipboard");
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  // Group calendars by hotel
  const groupedCalendars = calendars.reduce((acc, cal) => {
    if (!acc[cal.hotel_id]) {
      acc[cal.hotel_id] = { name: cal.hotel_name, cabins: [] };
    }
    acc[cal.hotel_id].cabins.push(cal);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-white px-4 py-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center text-white/80 hover:text-white mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-display text-2xl font-bold flex items-center gap-2">
                <Calendar className="w-6 h-6" />
                Calendar Sync
              </h1>
              <p className="text-white/80 text-sm">
                Import from Airbnb, VRBO & Booking.com • Export to sync back
              </p>
            </div>
            <Button 
              onClick={handleSyncAll} 
              disabled={syncing}
              className="bg-white/20 hover:bg-white/30"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync All
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {Object.entries(groupedCalendars).map(([hotelId, hotel]) => (
          <Card key={hotelId} className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Home className="w-5 h-5 text-primary" />
                {hotel.name}
              </CardTitle>
              <CardDescription>{hotel.cabins.length} cabin(s)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {hotel.cabins.map((cal) => {
                const isEditing = editingCabin === `${cal.hotel_id}/${cal.cabin_id}`;
                const hasUrls = cal.airbnb_url || cal.vrbo_url || cal.booking_url;
                
                return (
                  <div 
                    key={cal.cabin_id}
                    className="border border-border rounded-lg p-4 bg-background/50"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">{cal.cabin_name}</h4>
                        <div className="flex gap-2 mt-1">
                          {cal.airbnb_url && <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">Airbnb</Badge>}
                          {cal.vrbo_url && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">VRBO</Badge>}
                          {cal.booking_url && <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">Booking</Badge>}
                          {!hasUrls && <Badge variant="outline" className="text-xs">No calendars linked</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {hasUrls && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSyncCabin(cal.hotel_id, cal.cabin_id)}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Sync
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={isEditing ? "default" : "outline"}
                          onClick={() => isEditing ? handleSave(cal.hotel_id, cal.cabin_id) : handleEdit(cal)}
                        >
                          {isEditing ? "Save" : "Edit URLs"}
                        </Button>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="space-y-3 mt-4 pt-4 border-t border-border">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Airbnb iCal URL</Label>
                          <Input
                            value={editUrls.airbnb_url}
                            onChange={(e) => setEditUrls({ ...editUrls, airbnb_url: e.target.value })}
                            placeholder="https://www.airbnb.com/calendar/ical/..."
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">VRBO iCal URL</Label>
                          <Input
                            value={editUrls.vrbo_url}
                            onChange={(e) => setEditUrls({ ...editUrls, vrbo_url: e.target.value })}
                            placeholder="https://www.vrbo.com/icalendar/..."
                            className="text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Booking.com iCal URL</Label>
                          <Input
                            value={editUrls.booking_url}
                            onChange={(e) => setEditUrls({ ...editUrls, booking_url: e.target.value })}
                            placeholder="https://admin.booking.com/ical/..."
                            className="text-sm"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingCabin(null)}
                          className="text-muted-foreground"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* Export URL */}
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Export URL (add to Airbnb/VRBO):</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyExportUrl(cal.export_url)}
                          className="h-7 text-xs"
                        >
                          {copiedUrl === cal.export_url ? (
                            <><Check className="w-3 h-3 mr-1 text-green-600" /> Copied</>
                          ) : (
                            <><Copy className="w-3 h-3 mr-1" /> Copy URL</>
                          )}
                        </Button>
                      </div>
                      {cal.last_sync && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Last synced: {format(new Date(cal.last_sync), "MMM d, yyyy h:mm a")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}

        {/* Instructions */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-blue-900 text-base">How to Get iCal URLs</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-blue-800 space-y-2">
            <p><strong>Airbnb:</strong> Go to Calendar → Availability settings → "Export Calendar" to get the iCal link</p>
            <p><strong>VRBO:</strong> Go to Calendar → Import/Export → "Export your VRBO calendar" for the link</p>
            <p><strong>Booking.com:</strong> Go to Calendar → Sync calendars → Copy the export URL</p>
            <p className="pt-2 text-blue-700">
              <strong>Tip:</strong> After adding URLs here, copy the Export URL and import it back into Airbnb/VRBO/Booking to sync both ways!
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
